import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OccasionCard = ({ occasion, onEdit, onDelete, onViewGifts }) => {
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'birthday': return 'Cake';
      case 'anniversary': return 'Heart';
      case 'holiday': return 'Star';
      case 'graduation': return 'GraduationCap';
      case 'wedding': return 'Rings';
      case 'baby': return 'Baby';
      default: return 'Calendar';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'birthday': return 'secondary';
      case 'anniversary': return 'primary';
      case 'holiday': return 'warning';
      case 'graduation': return 'success';
      case 'wedding': return 'primary';
      case 'baby': return 'secondary';
      default: return 'accent';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    const occasionDate = new Date(dateString);
    const diffTime = occasionDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Past';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil === 'Past') return 'text-accent-400';
    if (daysUntil === 'Today') return 'text-error-600';
    if (daysUntil === 'Tomorrow') return 'text-warning-600';
    const days = parseInt(daysUntil);
    if (days <= 7) return 'text-warning-600';
    if (days <= 30) return 'text-secondary-600';
    return 'text-accent-600';
  };

  const categoryColor = getCategoryColor(occasion.category);
  const daysUntil = getDaysUntil(occasion.date);

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 hover:shadow-warm transition-all duration-300 hover-lift">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            categoryColor === 'primary' ? 'bg-primary-100 text-primary-600' :
            categoryColor === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
            categoryColor === 'warning' ? 'bg-warning-100 text-warning-600' :
            categoryColor === 'success'? 'bg-success-100 text-success-600' : 'bg-accent-100 text-accent-600'
          }`}>
            <Icon name={getCategoryIcon(occasion.category)} size={24} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-accent-800">
              {occasion.title}
            </h3>
            <p className="text-sm text-accent-600 capitalize">
              {occasion.category.replace('_', ' ')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="Edit"
            onClick={() => onEdit(occasion)}
            className="text-accent-500 hover:text-accent-700"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Trash2"
            onClick={() => onDelete(occasion.id)}
            className="text-error-500 hover:text-error-700"
          />
        </div>
      </div>

      {/* Date and Countdown */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-accent-600">
            {formatDate(occasion.date)}
          </p>
          <span className={`text-sm font-medium ${getUrgencyColor(daysUntil)}`}>
            {daysUntil}
          </span>
        </div>
      </div>

      {/* Recipients */}
      {occasion.recipients && occasion.recipients.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-accent-600 mb-2">Recipients:</p>
          <div className="flex flex-wrap gap-2">
            {occasion.recipients.map((recipient, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-surface text-accent-700"
              >
                <Icon name="User" size={12} className="mr-1" />
                {recipient}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Budget */}
      {occasion.budget && (
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-accent-600">Budget:</span>
            <span className="text-sm font-medium text-accent-800">
              ${occasion.budget}
            </span>
          </div>
        </div>
      )}

      {/* Gift Status */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-accent-600">Gift Planning:</span>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            occasion.giftStatus === 'completed' ? 'bg-success-100 text-success-700' :
            occasion.giftStatus === 'in_progress'? 'bg-warning-100 text-warning-700' : 'bg-accent-100 text-accent-700'
          }`}>
            {occasion.giftStatus === 'completed' ? 'Ready' :
             occasion.giftStatus === 'in_progress'? 'In Progress' : 'Not Started'}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-accent-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              occasion.giftStatus === 'completed' ? 'bg-success-400 w-full' :
              occasion.giftStatus === 'in_progress'? 'bg-warning-400 w-2/3' : 'bg-accent-300 w-1/4'
            }`}
          ></div>
        </div>
      </div>

      {/* Notes */}
      {occasion.notes && (
        <div className="mb-4">
          <p className="text-sm text-accent-600 mb-1">Notes:</p>
          <p className="text-sm text-accent-700 bg-surface p-3 rounded-lg">
            {occasion.notes}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center space-x-3 pt-4 border-t border-accent-100">
        <Button
          variant="primary"
          size="sm"
          iconName="Gift"
          iconPosition="left"
          onClick={() => onViewGifts(occasion)}
          className="flex-1"
        >
          View Gifts
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="Bell"
          onClick={() => {/* Handle reminder setup */}}
          className="text-accent-600"
        >
          Remind Me
        </Button>
      </div>
    </div>
  );
};

export default OccasionCard;